/* eslint-disable react/no-array-index-key */
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import React from 'react';

// 检验报告项目接口
interface InspectionItem {
  itemName: string;
  resultValueItem: string;
  unit?: string;
  status: number;
  referenceItem?: string;
}

// 病理报告接口
interface PathologyDescription {
  immunohistochemistry?: string;
  summary?: string;
}

interface MedicalRecord {
  type: string;
  recordTime: string;
  data: any;
  displayFields: Record<string, any>;
}

// 定义每个类型的展示字段
const displayFieldsMap: Record<string, string[]> = {
  medicalRecord: ['recordTime', 'chiefComplaint', 'diagnosis', 'presentIllness'],
  hospitalDischargeRecords: ['dischargeTime', 'dischargeDiagnosis', 'admitDescription'],
  imageReport: ['recordTime', 'parts', 'imagesType', 'reportDiagnose'],
  surgicalRecord: ['recordTime', 'surgeryName', 'procedures'],
  pathologyReport: ['recordTime', 'summary', 'immunohistochemistry'],
  inspectionReport: ['recordTime', 'type', 'zhInspectionItems'],
  geneticTesting: ['recordTime', 'itemName', 'sampleType', 'description'],
  drugRecord: ['recordTime', 'drugPlan', 'adverseReactions', 'treatmentEffect'],
};

// 格式化日期
const formatDate = (date: string) => {
  if (!date) return '';
  const dateObj = new Date(date);
  return format(dateObj, 'yyyy-MM', { locale: zhCN });
};

// 检验状态颜色定义
const StatusColor: Record<number, string> = {
  0: '#204568', // 正常
  1: '#F53F3F', // 异常
  2: '#40BA32', // 偏低
  3: '#F53F3F', // 偏高
};

// 处理检验报告项目的状态
const getInspectionItemStatus = (status: number): string => {
  switch (status) {
    case 1:
      return '异常';
    case 2:
      return '↓';
    case 3:
      return '↑';
    default:
      return '';
  }
};

// 处理检验报告的特殊字段
export const parseInspectionReport = (record: any) => {
  try {
    if (record.zhInspectionItems && Array.isArray(record.zhInspectionItems)) {
      const abnormalItems = record.zhInspectionItems.map((item: InspectionItem, index: number) => {
        const status = getInspectionItemStatus(item.status);
        const color = StatusColor[item.status] || StatusColor[0];
        return (
          <span key={`inspection-${item.itemName}-${item.status}-${index}`} style={{ color }}>
            {item.itemName}: {item.resultValueItem}
            {item.unit || ''} {status}
          </span>
        );
      });

      return {
        ...record,
        inspectionItems:
          abnormalItems.length > 0 ? (
            <span>
              {abnormalItems.map((item: React.ReactElement, index: number) => (
                <React.Fragment key={`fragment-${item.key || index}`}>
                  {item}
                  {index < abnormalItems.length - 1 && '；'}
                </React.Fragment>
              ))}
            </span>
          ) : (
            ''
          ),
      };
    }
  } catch (e) {
    console.warn('处理检验报告 zhInspectionItems 失败:', e);
  }
  return record;
};

// 处理病理报告的特殊字段
const parsePathologyReport = (record: any) => {
  try {
    if (record.diagnosisDescription) {
      const diagnosisData = JSON.parse(record.diagnosisDescription) as PathologyDescription;
      return {
        ...record,
        immunohistochemistry: diagnosisData.immunohistochemistry || '',
        summary: diagnosisData.summary || '',
      };
    }
  } catch (e) {
    console.warn('解析病理报告 diagnosisDescription 失败:', e);
  }
  return record;
};

// 转换单个记录
const transformRecord = (record: any, type: string): MedicalRecord => {
  const fields = displayFieldsMap[type] || [];

  // 对不同类型的记录进行特殊处理
  let processedRecord = record;
  switch (type) {
    case 'pathologyReport':
      processedRecord = parsePathologyReport(record);
      break;
    case 'inspectionReport':
      processedRecord = parseInspectionReport(record);
      break;
    default:
      break;
  }

  const displayFields = fields.reduce((acc, field) => {
    acc[field] = processedRecord[field] || '';
    return acc;
  }, {} as Record<string, any>);

  return {
    type,
    recordTime: processedRecord.recordTime || processedRecord.dischargeTime || '',
    data: processedRecord,
    displayFields,
  };
};

// 主格式化函数
const formatMedicalRecords = (data: any) => {
  const allRecords: MedicalRecord[] = [];

  // 处理各类记录
  const recordTypes = {
    MedicalRecords: 'medicalRecord',
    hospitalDischargeRecords: 'hospitalDischargeRecords',
    zhDrugRecords: 'drugRecord',
    zhGeneticTestings: 'geneticTesting',
    zhImageReports: 'imageReport',
    zhInspectionReports: 'inspectionReport',
    zhPathologyReports: 'pathologyReport',
    zhSurgicalRecords: 'surgicalRecord',
  } as const;

  Object.entries(recordTypes).forEach(([key, type]) => {
    if (data[key] && Array.isArray(data[key])) {
      data[key].forEach((record: any) => {
        allRecords.push(transformRecord(record, type));
      });
    }
  });

  // 按月份分组
  const groupedRecords = allRecords.reduce((acc, record) => {
    const monthKey = formatDate(record.recordTime);
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(record);
    return acc;
  }, {} as Record<string, MedicalRecord[]>);

  // 转换为数组并排序
  return Object.entries(groupedRecords)
    .map(([month, records]) => ({
      month,
      records,
    }))
    .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
};

export default formatMedicalRecords;
